import { PhaseDetail } from '@share/interfaces';
import { toJSTimestamp } from '@util/helper';
import { useCurrency, useHelper } from 'src/hooks';
import { useDebouncedCallback } from 'use-debounce';
import { useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { web3 } from '@state/web3';
import Countdown, { CountdownRenderProps } from 'react-countdown';
import MintModal from '@com/modals/MintModal';
import MouseOver from '@com/utils/MouseOver';
import styled from '@emotion/styled';

interface Props {
  collectionAddress: string;
  launchpadId: string;
  data: PhaseDetail;
}

const DisplayCardContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  grid-gap: 2em 3em;
`;

const DisplayCard = styled.div`
  background-color: var(--bg-section2);
  border-radius: 0.4em;
  padding: 1.5em 2.5em;
  text-align: center;
  height: 100%;

  div {
    margin-top: 1em;
    font-size: 1.8rem;
  }
`;

const Badge = styled.div`
  display: flex;
  align-items: center;
  font-weight: bold;
  background-color: var(--primary-color5);
  padding: 0.5em 1em;
  font-size: 1.8em;
  border-radius: 0.5em;
  color: var(--primary-color2);
  margin-right: 0.6rem;
`;

const Counter = styled.div`
  p {
    margin-right: 1rem;
    color: var(--primary-color2);
  }

  span {
    font-weight: bold;
    margin-right: 0.5rem;
  }
`;

const MintBtn = styled.button`
  margin-bottom: 3rem !important;
  margin-left: auto;
  font-size: 2em;
  padding: 0.45em 1.6em;
  :hover,
  :focus {
    color: #fff;
    opacity: 0.9;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

function LaunchpadPhraseCard({
  collectionAddress,
  launchpadId,
  data,
}: Props): JSX.Element {
  /* Define state / hook */
  const { t } = useTranslation();
  const { launchpadIdentifier, _paramsChainId } = useParams();
  const { displayCurrency, primaryColor } = useHelper();
  const mintModal = useRef<{ toggleModal: () => void }>(null);
  const { isValidRequest } = web3.useContainer();
  const { getCurrencyNameById } = useCurrency();

  /* Custom Data Object for render or other usages */
  const paramsChainId = Number(_paramsChainId);
  const config = data?.config;

  /* Render control function / logic */
  const toLocaleTimezone = (date: number) => {
    return new Date(date * 1000).toLocaleString();
  };

  const handleMint = useDebouncedCallback(
    async () => {
      if (!(await isValidRequest(paramsChainId)) || !launchpadIdentifier)
        return;
      mintModal.current?.toggleModal();
    },
    500,
    { leading: true, trailing: false }
  );

  /* Data Change Listeners - useEffect/useMemo/useCallback */

  /* Styling */

  const Container = useMemo(
    () => styled.div`
      backdrop-filter: blur(15px);
      background: ${`${primaryColor}cc`};
    `,
    [primaryColor]
  );

  /* Render */
  return (
    <>
      <Container className="sc-card-activity style1 flex-column align-items-start overflow-hidden">
        <div className="d-flex align-items-center flex-wrap w-100">
          <h2 className="mb-5 mr-4">{data?.name}</h2>
          <div className="status mb-5 d-flex mr-3">
            {Date.now() < toJSTimestamp(data?.end_at) ? (
              <Badge>
                <Countdown
                  date={toJSTimestamp(data?.start_at)}
                  renderer={(cProps: CountdownRenderProps) =>
                    !cProps.completed ? (
                      <div className="center">
                        <Counter className="d-flex flex-wrap justify-content-around font-weight-bold">
                          {cProps.days > 0 && (
                            <p>
                              <span>{cProps.days}</span>
                              {t('itemDetails.days')}
                            </p>
                          )}
                          <p>
                            <span>{cProps.hours}</span>
                            {t('itemDetails.hours')}
                          </p>
                          <p>
                            <span>{cProps.minutes}</span>
                            {t('itemDetails.minutes')}
                          </p>
                          <p>
                            <span>{cProps.seconds}</span>
                            {t('itemDetails.seconds')}
                          </p>
                        </Counter>
                      </div>
                    ) : (
                      <span className="text-success">
                        {t('launchpadPage.live')}
                      </span>
                    )
                  }
                />
              </Badge>
            ) : (
              <Badge>
                <span className="text-secondary">
                  {t('launchpadPage.ended')}
                </span>
              </Badge>
            )}
            {Boolean(data?.is_whitelist) && (
              <Badge>
                <span>{t('launchpadPage.whitelisted')}</span>
              </Badge>
            )}
          </div>
          {Date.now() > toJSTimestamp(data?.start_at) &&
            Date.now() < toJSTimestamp(data?.end_at) && (
              <MintBtn type="button" onClick={handleMint}>
                {t('launchpad.mintNow')}
              </MintBtn>
            )}
        </div>
        <DisplayCardContainer>
          <DisplayCard>
            <h3>{t('launchpad.price')}</h3>
            <div>
              {displayCurrency(
                config?.price,
                getCurrencyNameById(data?.payment_method?.currency?.id)
              )}
            </div>
          </DisplayCard>
          <MouseOver text={t('launchpad.timeDes')}>
            <DisplayCard>
              <h3>{t('launchpad.startAt')}</h3>
              <div>{toLocaleTimezone(data?.start_at)}</div>
            </DisplayCard>
          </MouseOver>
          <MouseOver text={t('launchpad.timeDes')}>
            <DisplayCard>
              <h3>{t('launchpad.endAt')}</h3>
              <div>{toLocaleTimezone(data?.end_at)}</div>
            </DisplayCard>
          </MouseOver>
          <MouseOver text={t('launchpad.userMaxDes')}>
            <DisplayCard>
              <h3>{t('launchpad.userMax')}</h3>
              <div>{config?.max}</div>
            </DisplayCard>
          </MouseOver>
          {/* TODO: minus previous phrase quota? */}
          <MouseOver text={t('launchpad.maxDes')}>
            <DisplayCard>
              <h3>{t('launchpad.max')}</h3>
              <div>{`${data?.minted} / ${config?.amount}`}</div>
            </DisplayCard>
          </MouseOver>
        </DisplayCardContainer>
      </Container>
      <MintModal
        ref={mintModal}
        launchpadAddress={collectionAddress}
        launchpadId={launchpadId}
        phase={data}
      />
    </>
  );
}

export default LaunchpadPhraseCard;
